import React, { useState } from "react";
import { FormSection, FormTemplate } from "../../../../types/forms";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/input2";
import { Trash2, ArrowLeft, Plus, Loader2, GripVertical } from "lucide-react";
import { Textarea } from "../../../../components/ui/Textarea";
import {
    addFields,
    deleteFieldOrSection,
    saveGeneralQuestion,
    updateFieldContent,
    reorderItems,
    getFormByUuid,
} from "../../../../services/consultationform";
import { useToast } from "../../../../hooks/useToast";
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
} from "@hello-pangea/dnd";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../components/ui/select";

interface SectionEditorProps {
    section: FormSection;
    updateTemplate: (
        update: (prev: FormTemplate) => FormTemplate
    ) => void;
    onBack: () => void;
    formUuid: string;
}

export default function SectionEditor({
    section,
    updateTemplate,
    onBack,
    formUuid,
}: SectionEditorProps) {
    const [isAddingField, setIsAddingField] = useState(false);
    const [newFieldLabel, setNewFieldLabel] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const showToast = useToast();

    // Specialized Question State
    // Specialized Question State
    const metadataLabels = ["Question Text", "Answer Type", "Required Field", "Help Text"];
    const dataField = section.fields?.find(f => !metadataLabels.includes(f.label));
    const qtField = section.fields?.find(f => f.label === "Question Text");
    const rfField = section.fields?.find(f => f.label === "Required Field");

    const [questionText, setQuestionText] = useState(() => {
        if (dataField) return dataField.label;
        return qtField?.options?.content || qtField?.content || "";
    });

    const [answerType, setAnswerType] = useState(() => {
        if (dataField) return dataField.type;
        const field = section.fields?.find(f => f.label === "Answer Type");
        const rawType = field?.options?.content || field?.content || "text";

        const typeMap: Record<string, string> = {
            "Multiple Choice": "radio",
            "Checkboxes": "checkbox_group",
            "Dropdown": "select",
            "Short Answer": "text",
            "Paragraph": "textarea",
            "Date": "date"
        };
        return typeMap[rawType] || rawType;
    });

    const [isRequired, setIsRequired] = useState<boolean>(() => {
        if (dataField) return !!dataField.required;
        return String(rfField?.options?.content) === "true" || String(rfField?.content) === "true";
    });

    const [options, setOptions] = useState<string[]>(() => {
        const opts = dataField?.options;
        return Array.isArray(opts) ? opts : [];
    });


    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateTemplate((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === section.id ? { ...s, title: e.target.value } : s
            ),
        }));
    };

    const handleAddField = async () => {
        if (!newFieldLabel.trim()) return;
        setIsSaving(true);
        try {
            const fieldData = {
                label: newFieldLabel,
                type: "text",
                required: false,
            };
            const response = await addFields(section.id, [fieldData]);

            updateTemplate((prev) => ({
                ...prev,
                sections: prev.sections.map((s) =>
                    s.id === section.id
                        ? { ...s, fields: [...(s.fields || []), ...response.fields] }
                        : s
                ),
            }));
            setNewFieldLabel("");
            setIsAddingField(false);
        } catch (err) {
            console.error("Failed to add field:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteField = async (fieldId: number) => {
        try {
            await deleteFieldOrSection("field", fieldId);
            updateTemplate((prev) => ({
                ...prev,
                sections: prev.sections.map((s) =>
                    s.id === section.id
                        ? { ...s, fields: s.fields?.filter((f) => f.id !== fieldId) }
                        : s
                ),
            }));
        } catch (err) {
            console.error("Failed to delete field:", err);
        }
    };

    const handleFieldLabelChange = (fieldId: string | number, newLabel: string) => {
        updateTemplate((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === section.id
                    ? {
                        ...s,
                        fields: s.fields?.map((f) =>
                            f.id === fieldId ? { ...f, label: newLabel } : f
                        ),
                    }
                    : s
            ),
        }));
    };

    const handleFieldContentChange = async (fieldId: string | number, content: string) => {
        updateTemplate((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === section.id
                    ? {
                        ...s,
                        fields: s.fields?.map((f) =>
                            f.id === fieldId ? { ...f, options: { ...(f.options || {}), content } } : f
                        ),
                    }
                    : s
            ),
        }));
        try {
            await updateFieldContent(fieldId, content);
        } catch (err) {
            console.error("Failed to update field content:", err);
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;
        const items = Array.from(section.fields || []);
        const [reordered] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reordered);

        updateTemplate((prev) => ({
            ...prev,
            sections: prev.sections.map((s) =>
                s.id === section.id ? { ...s, fields: items } : s
            ),
        }));

        try {
            const payload = items.map((f, idx) => ({ id: Number(f.id), order: idx + 1 }));
            await reorderItems(formUuid, { fields: payload });
        } catch (err) {
            console.error("Failed to reorder fields:", err);
        }
    };

    const handleSaveQuestion = async () => {
        setIsSaving(true);
        try {
            const data = {
                field_id: dataField?.id || qtField?.id,
                question: questionText,
                answerType,
                options,
                is_required: isRequired
            };
            // Save the question configuration
            await saveGeneralQuestion(section.id, data);

            // Refetch the full form to get the properly constructed section with fields
            // This avoids issues where the save API returns a partial object or just metadata
            const fullForm = await getFormByUuid(formUuid);
            const updatedSection = fullForm.form.sections.find((s: FormSection) => s.id === section.id);

            if (updatedSection) {
                // Filter out metadata fields immediately to keep local state clean
                const cleanedFields = (updatedSection.fields || []).filter(
                    (f: any) => !metadataLabels.includes(f.label)
                );

                updateTemplate((prev) => ({
                    ...prev,
                    sections: prev.sections.map((s) =>
                        s.id === section.id ? {
                            ...updatedSection,
                            fields: cleanedFields,
                            type: s.type
                        } : s
                    ),
                }));
                showToast.success("Question updated successfully!");
                onBack(); // Go back to the main list
            } else {
                console.error("Could not find updated section in refetched form");
                showToast.error("Saved, but failed to refresh form data.");
            }
        } catch (err: any) {
            console.error("Failed to save question:", err);
            showToast.error("Failed to save question");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 mb-4">
                <button onClick={onBack} className="text-gray-500 hover:text-gray-800">
                    <ArrowLeft size={16} />
                </button>
                <h3 className="font-semibold text-gray-800 uppercase tracking-wider text-xs">Section Customizer</h3>
            </div>

            <div className="space-y-4 flex-1 overflow-y-auto pr-2 custom-scrollbar">

                {section.type === "question" ? (
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">
                                Question Text
                            </label>
                            <Input
                                value={questionText}
                                onChange={(e) => setQuestionText(e.target.value)}
                                placeholder="Enter your question..."
                                className="h-9 text-xs"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 mb-1 uppercase">
                                Response Type
                            </label>
                            <Select value={answerType} onValueChange={setAnswerType}>
                                <SelectTrigger className="w-full h-9 bg-white border-gray-200 text-xs">
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    <SelectItem value="text">Short Answer</SelectItem>
                                    <SelectItem value="textarea">Paragraph</SelectItem>
                                    <SelectItem value="radio">Multiple Choice</SelectItem>
                                    <SelectItem value="checkbox_group">Checkboxes</SelectItem>
                                    <SelectItem value="select">Dropdown</SelectItem>
                                    <SelectItem value="date">Date</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {["radio", "checkbox_group", "select"].includes(answerType) && (
                            <div className="space-y-2">
                                <label className="block text-[10px] font-bold text-gray-500 uppercase">
                                    Options
                                </label>
                                {options.map((opt, idx) => (
                                    <div key={idx} className="flex gap-2">
                                        <Input
                                            value={opt}
                                            onChange={(e) => {
                                                const newOpts = [...options];
                                                newOpts[idx] = e.target.value;
                                                setOptions(newOpts);
                                            }}
                                            className="h-8 text-[12px]"
                                        />
                                        <button
                                            onClick={() => setOptions(options.filter((_, i) => i !== idx))}
                                            className="text-gray-400 hover:text-red-500"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => setOptions([...options, ""])}
                                    className="text-[#885ABB] text-[10px] font-medium hover:underline"
                                >
                                    + Add Option
                                </button>
                            </div>
                        )}

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={isRequired}
                                onChange={(e) => setIsRequired(e.target.checked)}
                                id="isRequired"
                                className="accent-[#885ABB]"
                            />
                            <label htmlFor="isRequired" className="text-[11px] text-gray-700 font-medium">
                                Mark as Mandatory
                            </label>
                        </div>

                        <Button
                            disabled={isSaving}
                            onClick={handleSaveQuestion}
                            className="w-full h-9 bg-[#885ABB] text-white hover:bg-purple-700 text-xs rounded-md shadow-sm"
                        >
                            {isSaving ? <Loader2 size={14} className="animate-spin" /> : "Apply Changes"}
                        </Button>
                    </div>
                ) : (
                    <div className="pt-4 border-t border-gray-100">
                        <div className="flex justify-between items-center mb-3">
                            <p className="text-[10px] font-bold text-gray-500 uppercase">Manage Fields</p>
                            {section.type !== "contact" && section.type !== "policy" && section.type !== "disclaimer" && section.type !== "text" ? (
                                <button
                                    onClick={() => setIsAddingField(true)}
                                    className="bg-purple-50 text-[#885ABB] hover:bg-purple-100 px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 transition-colors"
                                >
                                    <Plus size={10} /> Add Field
                                </button>
                            ) : (
                                <span className="text-[9px] text-gray-400 italic">Restricted section</span>
                            )}
                        </div>
                        {(section.type === "contact" || section.type === "policy" || section.type === "disclaimer" || section.type === "text") && (
                            <p className="text-[10px] text-gray-400 mb-3 bg-gray-50 p-2 rounded border border-gray-100 italic">
                                Note: {section.type === "contact" ? "You can remove fields you don't need, but you cannot add new ones." : "Editing for this section is done directly in the preview side. You cannot add new fields here."}
                            </p>
                        )}

                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="fields">
                                {(provided) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="space-y-2.5"
                                    >
                                        {section.fields?.map((field, index) => (
                                            <Draggable
                                                key={field.id}
                                                draggableId={String(field.id)}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`group flex flex-col p-2.5 bg-gray-50/50 rounded-lg border border-gray-100 hover:border-purple-200 transition-all ${snapshot.isDragging ? "shadow-lg bg-white border-[#885ABB] z-50 scale-[1.02]" : ""
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <div {...provided.dragHandleProps} className="text-gray-300 cursor-grab active:cursor-grabbing">
                                                                <GripVertical size={14} />
                                                            </div>
                                                            <div className="flex-1">
                                                                <Input
                                                                    value={field.label}
                                                                    onChange={(e) => handleFieldLabelChange(field.id, e.target.value)}
                                                                    className="h-7 text-[12px] bg-transparent border-none focus:ring-0 px-0 font-semibold text-gray-800"
                                                                    placeholder="Field Label"
                                                                />
                                                                <div className="text-[9px] text-[#885ABB] font-black uppercase tracking-tighter opacity-70">
                                                                    {field.type}
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => handleDeleteField(Number(field.id))}
                                                                className="text-gray-300 hover:text-red-500 transition-opacity"
                                                            >
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </div>

                                                        {["heading", "text", "textarea"].includes(field.type) && !["contact", "policy", "disclaimer", "text"].includes(section.type as string) && (
                                                            <div className="mt-2.5 pt-2.5 border-t border-gray-100/50">
                                                                <Textarea
                                                                    value={field.options?.content || ""}
                                                                    onChange={(e) => handleFieldContentChange(field.id, e.target.value)}
                                                                    placeholder={`Edit ${field.type} content...`}
                                                                    className="text-[12px] min-h-[70px] bg-white/80 border-gray-100 focus:border-purple-200"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}

                                        {isAddingField && (
                                            <div className="p-3 bg-purple-50/50 rounded-lg border border-purple-100 animate-in fade-in zoom-in-95 duration-200">
                                                <Input
                                                    autoFocus
                                                    value={newFieldLabel}
                                                    onChange={(e) => setNewFieldLabel(e.target.value)}
                                                    placeholder="What's the field label?"
                                                    className="h-8 text-[12px] mb-2.5"
                                                />
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        onClick={() => setIsAddingField(false)}
                                                        className="h-7 px-3 text-[10px] bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 font-bold"
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        disabled={isSaving}
                                                        onClick={handleAddField}
                                                        className="h-7 px-4 text-[10px] bg-[#885ABB] text-white hover:bg-purple-700 font-bold shadow-sm"
                                                    >
                                                        {isSaving ? <Loader2 size={12} className="animate-spin" /> : "Confirm Add"}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {(!section.fields || section.fields.length === 0) && !isAddingField && (
                                            <div className="text-center py-6 px-4 bg-gray-50/30 rounded-lg border border-dashed border-gray-200">
                                                <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest italic">
                                                    No custom fields yet
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                )}
            </div>
        </div>
    );
}
